// Budget Commander — game data
// All money amounts are whole dollars (not cents) for simplicity in the UI.
// They're converted to cents only when saved to Supabase (profiles.net_worth_cents).

export const MONTHLY_INCOME = 3000;
export const STARTING_DEBT = 1200;
export const DEBT_APR = 0.24;
export const STARTING_EMERGENCY_SAVINGS = 0;
export const EMERGENCY_FUND_GOAL = 600;

// ---------------------------------------------------------------------------
// STAGE 1 — INTEL: last month's real transactions, pulled from bank/credit
// statements. Category is pre-assigned (that part of tracking is done for
// them); the player's job is to tag Fixed/Variable and Need/Want, which is
// exactly the classification work real budgeting requires.
// ---------------------------------------------------------------------------
export const TRANSACTIONS = [
  { id: "t1", date: "Sep 1", description: "Rent Payment", amount: 1200, category: "ESSENTIALS", correctType: "FIXED", correctClass: "NEED" },
  { id: "t2", date: "Sep 3", description: "Electric Bill", amount: 95, category: "ESSENTIALS", correctType: "VARIABLE", correctClass: "NEED" },
  { id: "t3", date: "Sep 5", description: "Grocery Store", amount: 260, category: "ESSENTIALS", correctType: "VARIABLE", correctClass: "NEED" },
  { id: "t4", date: "Sep 7", description: "Streaming Bundle", amount: 28, category: "DISCRETIONARY", correctType: "FIXED", correctClass: "WANT" },
  { id: "t5", date: "Sep 9", description: "Gas Station", amount: 110, category: "ESSENTIALS", correctType: "VARIABLE", correctClass: "NEED" },
  { id: "t6", date: "Sep 10", description: "Concert Tickets", amount: 85, category: "DISCRETIONARY", correctType: "VARIABLE", correctClass: "WANT" },
  { id: "t7", date: "Sep 12", description: "Gym Membership", amount: 40, category: "LIFESTYLE", correctType: "FIXED", correctClass: "WANT" },
  { id: "t8", date: "Sep 15", description: "Credit Card — Minimum Payment", amount: 35, category: "SECURITY", correctType: "FIXED", correctClass: "NEED" },
  { id: "t9", date: "Sep 18", description: "Takeout Dinners (x4)", amount: 140, category: "DISCRETIONARY", correctType: "VARIABLE", correctClass: "WANT" },
  { id: "t10", date: "Sep 20", description: "Phone Bill", amount: 55, category: "ESSENTIALS", correctType: "FIXED", correctClass: "NEED" },
];

export const HISTORICAL_TOTAL = TRANSACTIONS.reduce((sum, t) => sum + t.amount, 0);
export const HISTORICAL_UNACCOUNTED = MONTHLY_INCOME - HISTORICAL_TOTAL;

export const CATEGORY_META = {
  ESSENTIALS: { label: "Essentials", flavor: "Rent, utilities, groceries, insurance — the non-negotiables.", accent: "cyan", icon: "🏠" },
  SECURITY: { label: "Security", flavor: "Emergency fund, debt payoff, and irregular-cost reserves.", accent: "lime", icon: "🛡️" },
  GOALS: { label: "Goals", flavor: "The big stuff you're saving toward on purpose.", accent: "gold", icon: "🚩" },
  LIFESTYLE: { label: "Lifestyle", flavor: "Gym, apparel, gifts, pet costs — social upkeep.", accent: "magenta", icon: "🎽" },
  DISCRETIONARY: { label: "Discretionary", flavor: "Games, dining out, treats — first place to cut if money's tight.", accent: "danger", icon: "🎮" },
};

// Suggested starting allocations, derived from the tagged historical data.
// The player can (and should) change every one of these.
export function computeBaselineSuggestions() {
  const byCategory = { ESSENTIALS: 0, LIFESTYLE: 0, DISCRETIONARY: 0 };
  TRANSACTIONS.forEach((t) => {
    if (byCategory[t.category] !== undefined) byCategory[t.category] += t.amount;
  });
  return {
    essentials: byCategory.ESSENTIALS,
    lifestyle: byCategory.LIFESTYLE,
    // Rounded to the nearest $10: the Planning stepper only moves in $10
    // increments, so any non-round default here would make the "Amount
    // Remaining" tracker mathematically unable to ever reach exactly $0.
    discretionary: Math.round(Math.max(0, byCategory.DISCRETIONARY - 100) / 10) * 10,
    emergencyFund: 100,
    debtPayoff: 60,
    sinkingFund: 50,
    goals: 0,
  };
}

// A hard floor for Essentials — you can't budget below what survival actually
// costs. Used to block unrealistic "just spend less on rent" allocations.
export const ESSENTIALS_FLOOR = TRANSACTIONS
  .filter((t) => t.category === "ESSENTIALS" && t.correctType === "FIXED")
  .reduce((sum, t) => sum + t.amount, 0);

// ---------------------------------------------------------------------------
// STAGE 3 — CAMPAIGN: choose-your-own-adventure decisions across the month.
// Each choice specifies which pool(s) it draws from. If a pool can't cover
// it, the UI falls back to the "credit card" option, which is always
// available but always costed as new debt.
// ---------------------------------------------------------------------------
export const SCENARIOS = [
  {
    id: "s1",
    week: "Week 1",
    title: "Grocery Run",
    text: "You're at the store with a list totaling $220. The bakery smells incredible and a rotisserie chicken combo catches your eye — another $45, easy.",
    choices: [
      {
        id: "list",
        label: "Stick to the list — $220",
        pool: "essentials",
        cost: 220,
        tag: "good",
        result: "You stuck to the plan. Intentional shopping keeps Essentials on track.",
      },
      {
        id: "extras",
        label: "Grab the extras — $265",
        pool: "essentials",
        cost: 265,
        tag: "risky",
        result: "Tasty, but that's $45 your plan didn't account for.",
      },
    ],
  },
  {
    id: "s2",
    week: "Week 1",
    title: "Concert Invite",
    text: "Friends are going to a show tonight. Tickets are $80.",
    choices: [
      {
        id: "pay-discretionary",
        label: "Pay from Discretionary — $80",
        pool: "discretionary",
        cost: 80,
        tag: "good",
        result: "This is exactly what your Discretionary fund is for — guilt-free fun.",
      },
      {
        id: "skip",
        label: "Skip it this time",
        pool: null,
        cost: 0,
        tag: "neutral",
        result: "No shame in sitting one out. The money stays put.",
      },
      {
        id: "credit",
        label: "Put it on a credit card",
        pool: "credit",
        cost: 80,
        tag: "bad",
        result: "Fun tonight, interest tomorrow. That's $80 of new debt.",
      },
    ],
  },
  {
    id: "s3",
    week: "Week 2",
    title: "Car Trouble",
    text: "Your check-engine light comes on. A shop quotes $400. A YouTube tutorial says you can likely fix it yourself for about $90 in parts.",
    choices: [
      {
        id: "emergency-fund",
        label: "Pay the shop from your Emergency Fund — $400",
        pool: "emergencyFund",
        cost: 400,
        tag: "good",
        result: "This is the exact scenario your Emergency Fund exists for — inconvenience, not disaster.",
      },
      {
        id: "diy",
        label: "Try the DIY fix — $90 from Sinking Fund",
        pool: "sinkingFund",
        cost: 90,
        tag: "good",
        result: "It worked. DIY saved you $310 versus the shop bill.",
      },
      {
        id: "credit",
        label: "Put the repair on a credit card",
        pool: "credit",
        cost: 400,
        tag: "bad",
        result: "The car's fixed, but you just added $400 in high-interest debt.",
      },
    ],
  },
  {
    id: "s4",
    week: "Week 2",
    title: "Annual Renewal",
    text: "Your gym's annual contract auto-renews today: $120, all at once. You knew this was coming... in theory.",
    choices: [
      {
        id: "sinking-fund",
        label: "Pay from your Sinking Fund — $120",
        pool: "sinkingFund",
        cost: 120,
        tag: "good",
        result: "That's exactly why you set aside money for irregular costs — no scramble, no stress.",
      },
      {
        id: "discretionary",
        label: "Pay from Discretionary — $120",
        pool: "discretionary",
        cost: 120,
        tag: "neutral",
        result: "Covered, but it ate into money you'd earmarked for fun.",
      },
      {
        id: "credit",
        label: "Put it on a credit card",
        pool: "credit",
        cost: 120,
        tag: "bad",
        result: "A predictable expense just became unpredictable debt.",
      },
    ],
  },
  {
    id: "s5",
    week: "Week 3",
    title: "Summer Power Spike",
    text: "The AC's been running nonstop — your electric bill comes in $40 over what you budgeted.",
    choices: [
      {
        id: "essentials",
        label: "Cover it from Essentials — $40",
        pool: "essentials",
        cost: 40,
        tag: "neutral",
        result: "Handled. Worth watching next month if this keeps happening.",
      },
      {
        id: "offpeak",
        label: "Cover it, and switch to an off-peak utility plan",
        pool: "essentials",
        cost: 40,
        tag: "good",
        result: "Paid the bill, and future bills should run lighter running big appliances off-peak.",
      },
    ],
  },
  {
    id: "s6",
    week: "Week 3",
    title: "Birthday Windfall",
    text: "A relative slips you $100 for your birthday. No obligations attached — it's yours to place anywhere.",
    choices: [
      {
        id: "debt",
        label: "Throw it at debt payoff",
        pool: "windfall-debt",
        cost: -100,
        tag: "good",
        result: "Extra debt payments are the highest-return move you can make — that interest stops accruing for good.",
      },
      {
        id: "goals",
        label: "Add it to your Goals fund",
        pool: "windfall-goals",
        cost: -100,
        tag: "good",
        result: "Straight toward the future you're building.",
      },
      {
        id: "spend",
        label: "Treat yourself — spend it",
        pool: "windfall-discretionary",
        cost: -100,
        tag: "neutral",
        result: "It was a gift — spending some of it guilt-free is fine in moderation.",
      },
    ],
  },
];
